import './index.scss'

import { useEffect, useState } from 'react'

import { PeopleImportState } from '@hooks/usePeopleImport'
import {
  PeopleImportJob,
  PeopleImportStatus,
  peopleImportProcessedCount
} from '@interfaces/integration.interface'

function statusCopy(status: PeopleImportStatus) {
  switch (status) {
    case PeopleImportStatus.QUEUED:
      return 'Waiting to start…';
    case PeopleImportStatus.INTERRUPTED:
      // A chunk reached its deadline; the job resumes from where it stopped.
      return 'Paused briefly. It will pick up where it left off.';
    default:
      return 'Importing…';
  }
}

function countCopy(job: PeopleImportJob) {
  const processed = peopleImportProcessedCount(job.counters);
  const total = job.estimatedTotal ?? null;

  if (total !== null && total > 0) {
    return `${processed.toLocaleString()} of ${total.toLocaleString()} people`;
  }

  return processed > 0 ? `${processed.toLocaleString()} people so far` : null;
}

// Only the parts that happened, so a church with no existing people isn't told
// "0 linked".
function summaryParts(job: PeopleImportJob) {
  const counters = job.counters;

  return [
    counters.created > 0 ? `${counters.created.toLocaleString()} added` : null,
    counters.claimed > 0 ? `${counters.claimed.toLocaleString()} linked to people already in Salt` : null,
    counters.updated > 0 ? `${counters.updated.toLocaleString()} updated` : null
  ].filter((part): part is string => part !== null);
}

export default function PeopleImportCard(props: {
  state: PeopleImportState;
  providerName: string;
  canStart: boolean;
  isStarting: boolean;
  error: string | null;
  onStart: () => void;
  onDismiss: () => void;
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const { state, providerName } = props;

  // A confirmation belongs to the moment it was asked for. Once the card moves
  // on -- started, finished, failed -- it shouldn't come back mid-question.
  useEffect(() => {
    setIsConfirming(false);
  }, [state.kind]);

  if (state.kind === 'hidden') {
    return null;
  }

  if (state.kind === 'inProgress') {
    const job = state.job;
    const processed = peopleImportProcessedCount(job.counters);
    const total = job.estimatedTotal ?? null;
    const fraction = total !== null && total > 0 ? Math.min(processed / total, 1) : null;
    const count = countCopy(job);

    return <section className='people-import-card' aria-live='polite'>
      <div className='people-import-heading'>
        <span className='people-import-spinner' aria-hidden='true' />
        <h2>Importing people from {providerName}</h2>
      </div>

      <p className='people-import-status'>{statusCopy(job.status)}</p>

      {fraction !== null ? <div
        className='people-import-progress'
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={total ?? undefined}
        aria-valuenow={processed}>
        <span style={{ width: `${fraction * 100}%` }} />
      </div> : null}

      {count ? <p className='people-import-count'>{count}</p> : null}

      <p className='people-import-note'>
        This may take a while. You can leave this page, and we'll email you once the import is complete.
      </p>
    </section>
  }

  if (state.kind === 'succeeded') {
    const parts = summaryParts(state.job);
    const failed = state.job.counters.failed;

    return <section className='people-import-card is-complete' aria-live='polite'>
      <div className='people-import-heading'>
        <h2>Import complete</h2>
      </div>

      <p className='people-import-status'>
        {parts.length > 0 ? `${parts.join(', ')}.` : `Everyone from ${providerName} is now in Salt.`}
        {failed > 0 ? ` ${failed.toLocaleString()} couldn't be imported.` : null}
      </p>

      <div className='people-import-actions'>
        <button type='button' className='secondary' onClick={props.onDismiss}>Done</button>
      </div>
    </section>
  }

  if (state.kind === 'failed') {
    return <section className='people-import-card is-failed'>
      <div className='people-import-heading'>
        <h2>The import from {providerName} didn't finish</h2>
      </div>

      <p className='people-import-status'>
        {state.job.failure?.message ?? 'Something went wrong partway through.'}
      </p>

      {props.error ? <p className='people-import-error'>{props.error}</p> : null}

      {props.canStart ? <div className='people-import-actions'>
        <button type='button' disabled={props.isStarting} onClick={props.onStart}>
          {props.isStarting ? 'Starting…' : 'Try Again'}
        </button>
      </div> : null}
    </section>
  }

  // Available: connected, and nothing imported yet.
  return <section className='people-import-card'>
    <div className='people-import-heading'>
      <h2>Import your people from {providerName}</h2>
    </div>

    {isConfirming ? <>
      <p className='people-import-status'>
        <strong>Start the import?</strong> Anyone already in Salt with a matching email will be linked and updated
        from {providerName}. This can't be undone.
      </p>

      {props.error ? <p className='people-import-error'>{props.error}</p> : null}

      <div className='people-import-actions'>
        <button type='button' disabled={props.isStarting} onClick={props.onStart}>
          {props.isStarting ? 'Starting…' : 'Start Import'}
        </button>

        <button
          type='button'
          className='secondary'
          disabled={props.isStarting}
          onClick={() => setIsConfirming(false)}>
          Cancel
        </button>
      </div>
    </> : <>
      <p className='people-import-status'>
        {providerName} is connected. Bring everyone over so they show up here, and they'll stay in sync from then on.
      </p>

      <div className='people-import-actions'>
        <button type='button' onClick={() => setIsConfirming(true)}>Import People</button>
      </div>
    </>}
  </section>
}
